// linkIdentityFlow — the deterministic, version-independent core of the
// "Link Facebook / Link X" action on the Edit-Profile surface.
//
// WHY this exists as a standalone helper (not inline in AuthContext):
// the live bug was a SILENT nothing — tapping "Link Facebook" left the button
// stuck on "Linking…" with no redirect and no error. Pulling the decision
// logic into this pure function lets us unit-test all outcomes
// (url / provider-error / thrown / neither) without a browser or supabase-js.
//
// resolveLinkIdentityUrl is the mobile-safe flow: it does the authenticated
// linkIdentity call and RETURNS the provider URL so the UI can render a REAL
// <a href> the user physically taps.
//
// WHY (proven on-device, Android): supabase.auth.linkIdentity returns a valid
// OAuth URL (error:none), but a PROGRAMMATIC redirect that runs AFTER the async
// call resolves — `window.location.href = url` — is SILENTLY BLOCKED by mobile
// browsers (Samsung Internet / Firefox Enhanced Tracking Protection treat the
// post-async cross-site navigation as a tracking redirect, and/or the user-
// activation window is gone across the async gap). Desktop is unaffected. The
// login button (signInWithOAuth) works because it builds its URL synchronously
// and navigates inside the same tap; linkIdentity cannot, because it needs an
// authenticated server round-trip first. The fix is to make the FINAL hop a
// genuine user click on an anchor — a true user activation is exempt from the
// anti-tracking redirect block and carries fresh activation — so this resolver
// does the async work and hands the URL back; navigation is the UI's job.
//
// Returns { url, error }:
//   success        → { url: <provider url>, error: null }
//   provider error → { url: null, error: <message> }
//   thrown         → { url: null, error: <message> }
//   neither        → { url: null, error: 'No redirect URL returned from linkIdentity' }
// It must NEVER throw and NEVER navigate.
export async function resolveLinkIdentityUrl({ provider, redirectTo, scopes, linkFn }) {
  try {
    // Attach `options.scopes` only when the provider declares one, otherwise
    // omit it (Supabase default). Facebook passes
    // 'public_profile' here to avoid the "Invalid Scopes: email" error.
    const options = { redirectTo, skipBrowserRedirect: true }
    if (scopes) options.scopes = scopes
    const { data, error } = await linkFn({ provider, options })
    if (error) return { url: null, error: error.message }
    if (data?.url) return { url: data.url, error: null }
    return { url: null, error: 'No redirect URL returned from linkIdentity' }
  } catch (e) {
    return { url: null, error: e?.message || String(e) }
  }
}
